/**
 * Match Phase Domain
 *
 * Manages the hierarchical session phase state machine on the client side.
 * The server is authoritative — this store is updated whenever the server
 * broadcasts a phase_change (OpCodePhaseChange = 10) message.
 *
 * Session lifecycle phases:
 *   'start_lobby' → 'playing' → 'end_lobby'
 *
 * Sub-states during 'playing' are now controlled by the session script runner
 * (OpCodeSessionScriptStep = 320). See match-session-script.ts.
 */

import { create } from 'zustand';
import type { PhaseChangePayload } from '../constants/op-codes';
import { abandonCommandInFlight } from './command-gate';

/** The session's phase, as the server names it (match_phase.go MatchPhase.String; client_parity_test.go holds the names). */
export type MatchPhase = PhaseChangePayload['phase'];

interface MatchPhaseState {
  /** Current session lifecycle phase. */
  phase: MatchPhase;
  /** UTC Unix timestamp when 'playing' begins (0 = no scheduled start). */
  scheduledStartUtc: number;
  /** UTC Unix timestamp when 'end_lobby' begins (0 = no scheduled end). */
  scheduledEndUtc: number;
  /**
   * UTC Unix timestamp when the room closes: the server's own close
   * (match_phase.go EndLobbyEndsAt), five minutes after the end lobby begins.
   */
  endLobbyEndUtc: number;
  /**
   * True once the first phase_change message has been received from the server.
   * Until then the UI should not render the game (to avoid showing the wrong
   * phase before the server confirms the state).
   */
  phaseReceived: boolean;

  /** Apply a phase_change payload from the server. */
  setPhase(payload: PhaseChangePayload): void;
  /** Reset to the default 'playing' state (called when leaving the match). */
  reset(): void;
}

export const useMatchPhaseStore = create<MatchPhaseState>((set) => ({
  phase: 'playing',
  scheduledStartUtc: 0,
  scheduledEndUtc: 0,
  endLobbyEndUtc: 0,
  phaseReceived: false,

  setPhase: (payload) =>
    set({
      phase: payload.phase,
      scheduledStartUtc: payload.scheduled_start_utc,
      scheduledEndUtc: payload.scheduled_end_utc,
      endLobbyEndUtc: payload.end_lobby_end_utc,
      phaseReceived: true,
    }),

  reset: () =>
    set({
      phase: 'playing',
      scheduledStartUtc: 0,
      scheduledEndUtc: 0,
      endLobbyEndUtc: 0,
      phaseReceived: false,
    }),
}));

/** A lobby: before a scheduled session starts, and once it has ended. */
export type LobbyKind = 'start' | 'end';

/**
 * The lobby a phase is, or null while playing. The store holds 'playing'
 * until the server has said otherwise, so a lobby is always the server's word.
 */
function lobbyOf(phase: MatchPhase): LobbyKind | null {
  return phase === 'start_lobby' ? 'start' : phase === 'end_lobby' ? 'end' : null;
}

/** The lobby the match is in, or null. */
export function useLobby(): LobbyKind | null {
  return lobbyOf(useMatchPhaseStore((s) => s.phase));
}

// ==================== HANDLERS ====================

/**
 * OpCodePhaseChange: the session's phase, as the server says it. The end
 * lobby answers no command (match-handler.go): one on its way as the match
 * ends is over, and its sender's wait with it.
 */
export function handlePhaseChange(data: PhaseChangePayload): void {
  useMatchPhaseStore.getState().setPhase(data);
  if (data.phase === 'end_lobby') abandonCommandInFlight();
}
